<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div v-if="alert.show" 
         :class="[
           'mb-6 p-4 rounded-md border',
           alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
           alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
           alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
         ]">
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">إدارة الفروع</h1>
        <p class="mt-2 text-sm text-gray-700">إدارة فروع وجمعيات التحفيظ</p>
      </div>
      <div class="mt-4 sm:mt-0 sm:mr-16 sm:flex-none">
        <button
          @click="openAddModal"
          class="px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor/90 transition-colors">
          إضافة فرع جديد
        </button>
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div class="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      <div class="relative">
        <input
          v-model="searchTerm"
          type="text"
          placeholder="البحث في الفروع..."
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-3"
        />
      </div>
      <div class="relative">
        <select
          v-model="filterActive"
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        >
          <option :value="null">جميع الفروع</option>
          <option :value="true">فروع نشطة فقط</option>
          <option :value="false">فروع غير نشطة</option>
        </select>
      </div>
      <div class="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg flex items-center">
        إجمالي الفروع: {{ tenants.length }}
      </div>
    </div>

    <!-- Tenants Table -->
    <div class="mt-8 flex flex-col">
      <div class="sm:flex-auto">
        <h1 class="text-lg font-medium text-gray-900">قائمة الفروع</h1>
        <p class="mt-2 text-xs text-gray-700"></p>
      </div>

      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-gray-600">جاري التحميل...</p>
      </div>

      <div v-else-if="filteredTenants.length === 0" class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 5h10"></path>
        </svg>
        <p class="mt-2 text-gray-600">
          {{ searchTerm || filterActive !== null ? 'لا توجد فروع تطابق البحث' : 'لا توجد فروع مسجلة' }}
        </p>
      </div>

      <div v-else class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-customHafadhColor">
              <thead class="bg-customHafadhColor text-center">
                <tr>
                  <th class="py-3.5 pr-4 pl-3 text-right text-sm font-semibold text-gray-900 sm:pr-6">#</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الصورة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">اسم الفرع</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الوصف</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الحالة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الإحصائيات</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">تعديل</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">حذف</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-for="(tenant, index) in filteredTenants" :key="tenant.id">
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm text-gray-900 sm:pr-6">{{ index + 1 }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    <div v-if="tenant.imageUrl || tenant.logoUrl || tenant.image_url || tenant.logo_url" class="h-10 w-10 flex-shrink-0">
                      <img
                        :src="tenant.imageUrl || tenant.logoUrl || tenant.image_url || tenant.logo_url"
                        :alt="tenant.name"
                        class="h-10 w-10 rounded-lg object-cover"
                      />
                    </div>
                    <div v-else class="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <svg class="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{{ tenant.name }}</td>
                  <td class="px-3 py-4 text-sm text-gray-500 max-w-xs truncate">{{ tenant.description || '-' }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span 
                      :class="[
                        'px-2 py-1 rounded-full text-xs font-medium',
                        (tenant.isActive || tenant.is_active) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      ]">
                      {{ (tenant.isActive || tenant.is_active) ? 'نشط' : 'غير نشط' }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    <button 
                      @click="viewStatistics(tenant.id)"
                      class="text-blue-600 hover:text-blue-800 text-xs">
                      عرض الإحصائيات
                    </button>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button class="enabled-button" @click="openEditModal(tenant)">تعديل</button>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button 
                      class="text-red-600 hover:text-red-800" 
                      @click="handleDelete(tenant)">
                      {{ (tenant.isActive || tenant.is_active) ? 'تعطيل' : 'حذف' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1" class="mt-6 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          عرض {{ pagination.from }} إلى {{ pagination.to }} من {{ pagination.total }} نتائج
        </div>
        <div class="flex gap-2">
          <button
            @click="changePage(pagination.currentPage - 1)"
            :disabled="pagination.currentPage === 1"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            السابق
          </button>
          <button
            @click="changePage(pagination.currentPage + 1)"
            :disabled="pagination.currentPage === pagination.totalPages"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            التالي
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="popup" dir="rtl">
      <div class="popup-content" style="max-width: 600px; max-height: 90vh; overflow-y: auto;">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
          {{ editMode ? 'تعديل الفرع' : 'إضافة فرع جديد' }}
        </h3>
        
        <form @submit.prevent="handleSave">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label for="name" class="block text-sm font-medium text-gray-700 mb-2">اسم الفرع *</label>
              <input
                v-model="formData.name"
                type="text"
                id="name"
                required
                placeholder="أدخل اسم الفرع"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label for="code" class="block text-sm font-medium text-gray-700 mb-2">رمز الفرع</label>
              <input
                v-model="formData.code"
                type="text"
                id="code"
                placeholder="سيتم إنشاؤه تلقائياً إذا تركت فارغاً"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label for="description" class="block text-sm font-medium text-gray-700 mb-2">الوصف</label>
              <textarea
                v-model="formData.description"
                id="description"
                rows="3"
                placeholder="أدخل وصف الفرع"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              ></textarea>
            </div>

            <div>
              <label for="image" class="block text-sm font-medium text-gray-700 mb-2">صورة الفرع</label>
              <input
                @change="handleImageChange"
                type="file"
                id="image"
                accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-customHafadhColor file:text-white hover:file:bg-customHafadhColor/90"
              />
              <p v-if="formData.imagePreview" class="mt-2">
                <img :src="formData.imagePreview" alt="Preview" class="h-32 w-32 object-cover rounded-md border border-gray-300" />
              </p>
              <p v-else-if="formData.imageUrl" class="mt-2">
                <img :src="formData.imageUrl" alt="Current" class="h-32 w-32 object-cover rounded-md border border-gray-300" />
              </p>
              <p class="mt-1 text-xs text-gray-500">الحجم الأقصى: 5MB | الصيغ المدعومة: JPEG, PNG, JPG, GIF, WEBP</p>
            </div>

            <div>
              <label for="logo" class="block text-sm font-medium text-gray-700 mb-2">شعار الفرع</label>
              <input
                @change="handleLogoChange"
                type="file"
                id="logo"
                accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-customHafadhColor file:text-white hover:file:bg-customHafadhColor/90"
              />
              <p v-if="formData.logoPreview" class="mt-2">
                <img :src="formData.logoPreview" alt="Preview" class="h-32 w-32 object-cover rounded-md border border-gray-300" />
              </p>
              <p v-else-if="formData.logoUrl" class="mt-2">
                <img :src="formData.logoUrl" alt="Current" class="h-32 w-32 object-cover rounded-md border border-gray-300" />
              </p>
              <p class="mt-1 text-xs text-gray-500">الحجم الأقصى: 5MB | الصيغ المدعومة: JPEG, PNG, JPG, GIF, WEBP</p>
            </div>

            <div>
              <label class="flex items-center">
                <input
                  v-model="formData.isActive"
                  type="checkbox"
                  class="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <span class="mr-2 text-sm text-gray-700">فرع نشط</span>
              </label>
            </div>

            <!-- About Us Section -->
            <div class="col-span-1 border-t border-gray-200 pt-4 mt-4">
              <h4 class="text-md font-semibold text-gray-900 mb-4">بيانات عن المقرأة</h4>
              
              <div class="space-y-4">
                <div>
                  <label for="vision" class="block text-sm font-medium text-gray-700 mb-2">الرؤية</label>
                  <textarea
                    v-model="formData.vision"
                    id="vision"
                    rows="2"
                    placeholder="مثال: نحو مقرأة قرآنيّة محفِّزة"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  ></textarea>
                </div>

                <div>
                  <label for="mission" class="block text-sm font-medium text-gray-700 mb-2">الرسالة</label>
                  <textarea
                    v-model="formData.mission"
                    id="mission"
                    rows="3"
                    placeholder="مثال: نسعى إلى الإبداع والتميز في تعليم وخدمة حَمَلة القرآن الكريم عبر أحدث الوسائل الرقمية"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  ></textarea>
                </div>

                <div>
                  <label for="goal" class="block text-sm font-medium text-gray-700 mb-2">الهدف</label>
                  <textarea
                    v-model="formData.goal"
                    id="goal"
                    rows="2"
                    placeholder="مثال: أن يسمع الدارس القرآن كاملاً عن ظهر قلب في يوم واحد بجودة وإتقان"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-6 grid grid-cols-2 gap-3">
            <button
              type="submit"
              :disabled="saving"
              class="enabled-button w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:opacity-50">
              {{ saving ? 'جاري الحفظ...' : 'حفظ' }}
            </button>
            <button
              type="button"
              @click="closeModal"
              class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm">
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Statistics Modal -->
    <div v-if="showStatisticsModal" class="popup" dir="rtl">
      <div class="popup-content" style="max-width: 500px;">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">إحصائيات الفرع</h3>
        
        <div v-if="statisticsLoading" class="text-center py-8">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p class="mt-2 text-gray-600">جاري التحميل...</p>
        </div>

        <div v-else-if="statistics" class="space-y-4">
          <div class="bg-blue-50 p-4 rounded-lg">
            <p class="text-sm font-medium text-gray-700">اسم الفرع:</p>
            <p class="text-lg font-semibold text-gray-900">{{ statistics.tenantName }}</p>
          </div>
          
          <div class="grid grid-cols-2 gap-4">
            <div class="bg-white p-4 rounded-lg border border-gray-200">
              <p class="text-sm text-gray-600">إجمالي المستخدمين</p>
              <p class="text-2xl font-bold text-gray-900">{{ statistics.totalUsers || 0 }}</p>
            </div>
            <div class="bg-white p-4 rounded-lg border border-gray-200">
              <p class="text-sm text-gray-600">المستخدمون النشطون</p>
              <p class="text-2xl font-bold text-green-600">{{ statistics.activeUsers || 0 }}</p>
            </div>
            <div class="bg-white p-4 rounded-lg border border-gray-200">
              <p class="text-sm text-gray-600">إجمالي الطلاب</p>
              <p class="text-2xl font-bold text-blue-600">{{ statistics.totalStudents || 0 }}</p>
            </div>
            <div class="bg-white p-4 rounded-lg border border-gray-200">
              <p class="text-sm text-gray-600">إجمالي المعلمين</p>
              <p class="text-2xl font-bold text-purple-600">{{ statistics.totalTeachers || 0 }}</p>
            </div>
          </div>
        </div>

        <div class="mt-6">
          <button
            @click="closeStatisticsModal"
            class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm">
            إغلاق
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import axios from 'axios'
import url from '@/url.js'

export default defineComponent({
  name: 'TenantsManagement',
  data() {
    return {
      tenants: [],
      loading: false,
      saving: false,
      searchTerm: '',
      filterActive: null,
      showModal: false,
      editMode: false,
      showStatisticsModal: false,
      statisticsLoading: false,
      statistics: null,
      formData: {
        name: '',
        code: '',
        description: '',
        imageUrl: '',
        logoUrl: '',
        imageFile: null,
        logoFile: null,
        imagePreview: null,
        logoPreview: null,
        isActive: true,
        vision: '',
        mission: '',
        goal: ''
      },
      currentTenantId: null,
      alert: {
        show: false,
        type: 'success',
        message: ''
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        total: 0,
        from: 0,
        to: 0
      }
    }
  },
  computed: {
    filteredTenants() {
      let filtered = this.tenants

      // Filter by search term
      if (this.searchTerm) {
        const search = this.searchTerm.toLowerCase()
        filtered = filtered.filter(tenant => 
          tenant.name?.toLowerCase().includes(search) ||
          tenant.description?.toLowerCase().includes(search) ||
          tenant.code?.toLowerCase().includes(search)
        )
      }

      // Filter by active status
      if (this.filterActive !== null) {
        filtered = filtered.filter(tenant => (tenant.isActive || tenant.is_active) === this.filterActive)
      }

      return filtered
    }
  },
  methods: {
    async loadTenants() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          size: 10,
          search: this.searchTerm || undefined,
          is_active: this.filterActive !== null ? this.filterActive : undefined
        }

        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.tenants_list,
          {
            params,
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )

        // Handle different response structures
        let responseData = null
        if (response.data?.result?.data) {
          responseData = response.data.result.data
        } else if (response.data?.data) {
          responseData = response.data.data
        } else if (response.data?.result) {
          responseData = response.data.result
        } else {
          responseData = response.data
        }

        if (responseData) {
          // Check if it's a paginated response
          if (responseData.pagination) {
            this.tenants = Array.isArray(responseData.data) ? responseData.data : []
            const pag = responseData.pagination
            this.pagination = {
              currentPage: pag.current_page || 1,
              totalPages: pag.last_page || 1,
              total: pag.total || 0,
              from: ((pag.current_page || 1) - 1) * (pag.per_page || 10) + 1,
              to: Math.min((pag.current_page || 1) * (pag.per_page || 10), pag.total || 0)
            }
          } else if (responseData.content !== undefined) {
            // Spring Boot style pagination
            this.tenants = Array.isArray(responseData.content) ? responseData.content : []
            this.pagination = {
              currentPage: responseData.number + 1 || 1,
              totalPages: responseData.totalPages || 1,
              total: responseData.totalElements || 0,
              from: (responseData.number || 0) * (responseData.size || 10) + 1,
              to: Math.min((responseData.number + 1 || 1) * (responseData.size || 10), responseData.totalElements || 0)
            }
          } else if (Array.isArray(responseData)) {
            // Simple array response
            this.tenants = responseData
            this.pagination = {
              currentPage: 1,
              totalPages: 1,
              total: responseData.length,
              from: 1,
              to: responseData.length
            }
          } else {
            this.tenants = []
          }
        } else {
          this.tenants = []
        }
      } catch (error) {
        console.error('Failed to load tenants', error)
        this.showAlert('danger', error.response?.data?.mesAr || 'فشل في تحميل الفروع')
      } finally {
        this.loading = false
      }
    },
    openAddModal() {
      this.editMode = false
      this.formData = {
        name: '',
        code: '',
        description: '',
        imageUrl: '',
        logoUrl: '',
        imageFile: null,
        logoFile: null,
        imagePreview: null,
        logoPreview: null,
        isActive: true,
        vision: '',
        mission: '',
        goal: ''
      }
      this.showModal = true
    },
    openEditModal(tenant) {
      this.editMode = true
      this.formData = {
        name: tenant.name || '',
        code: tenant.code || '',
        description: tenant.description || '',
        imageUrl: tenant.imageUrl || tenant.image_url || '',
        logoUrl: tenant.logoUrl || tenant.logo_url || '',
        imageFile: null,
        logoFile: null,
        imagePreview: null,
        logoPreview: null,
        isActive: tenant.isActive !== undefined ? tenant.isActive : (tenant.is_active !== undefined ? tenant.is_active : true),
        vision: tenant.vision || '',
        mission: tenant.mission || '',
        goal: tenant.goal || ''
      }
      this.currentTenantId = tenant.id
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.editMode = false
      this.currentTenantId = null
      this.formData = {
        name: '',
        code: '',
        description: '',
        imageUrl: '',
        logoUrl: '',
        imageFile: null,
        logoFile: null,
        imagePreview: null,
        logoPreview: null,
        isActive: true,
        vision: '',
        mission: '',
        goal: ''
      }
      // Reset file inputs
      const imageInput = document.getElementById('image')
      const logoInput = document.getElementById('logo')
      if (imageInput) imageInput.value = ''
      if (logoInput) logoInput.value = ''
    },
    handleImageChange(event) {
      const file = event.target.files[0]
      if (file) {
        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          this.showAlert('danger', 'حجم الصورة يجب أن يكون أقل من 5MB')
          event.target.value = ''
          return
        }
        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp']
        if (!validTypes.includes(file.type)) {
          this.showAlert('danger', 'نوع الملف غير مدعوم. يرجى اختيار صورة (JPEG, PNG, JPG, GIF, WEBP)')
          event.target.value = ''
          return
        }
        this.formData.imageFile = file
        // Create preview
        const reader = new FileReader()
        reader.onload = (e) => {
          this.formData.imagePreview = e.target.result
        }
        reader.readAsDataURL(file)
        console.log('Image file selected:', file.name, file.size, 'bytes')
      } else {
        this.formData.imageFile = null
        this.formData.imagePreview = null
      }
    },
    handleLogoChange(event) {
      const file = event.target.files[0]
      if (file) {
        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
          this.showAlert('danger', 'حجم الشعار يجب أن يكون أقل من 5MB')
          event.target.value = ''
          return
        }
        // Validate file type
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp']
        if (!validTypes.includes(file.type)) {
          this.showAlert('danger', 'نوع الملف غير مدعوم. يرجى اختيار صورة (JPEG, PNG, JPG, GIF, WEBP)')
          event.target.value = ''
          return
        }
        this.formData.logoFile = file
        // Create preview
        const reader = new FileReader()
        reader.onload = (e) => {
          this.formData.logoPreview = e.target.result
        }
        reader.readAsDataURL(file)
        console.log('Logo file selected:', file.name, file.size, 'bytes')
      } else {
        this.formData.logoFile = null
        this.formData.logoPreview = null
      }
    },
    async handleSave() {
      this.saving = true
      try {
        // Use FormData to support file uploads
        const formData = new FormData()
        formData.append('name', this.formData.name)
        formData.append('description', this.formData.description || '')
        formData.append('is_active', this.formData.isActive ? '1' : '0')

        if (this.formData.code) {
          formData.append('code', this.formData.code)
        }

        // Add About Us fields
        if (this.formData.vision) {
          formData.append('vision', this.formData.vision)
        }
        if (this.formData.mission) {
          formData.append('mission', this.formData.mission)
        }
        if (this.formData.goal) {
          formData.append('goal', this.formData.goal)
        }

        // Add image file if selected
        if (this.formData.imageFile) {
          formData.append('image', this.formData.imageFile)
        } else if (this.formData.imageUrl && !this.formData.imageFile) {
          // Keep existing URL if no new file is selected
          formData.append('image_url', this.formData.imageUrl)
        }

        // Add logo file if selected
        if (this.formData.logoFile) {
          formData.append('logo', this.formData.logoFile)
        } else if (this.formData.logoUrl && !this.formData.logoFile) {
          // Keep existing URL if no new file is selected
          formData.append('logo_url', this.formData.logoUrl)
        }

        // Don't set Content-Type manually - let axios set it automatically with boundary
        const headers = {
          'X-Authorization': localStorage.getItem('token')
        }

        // Log FormData contents for debugging
        console.log('FormData contents before send:', {
          name: this.formData.name,
          description: this.formData.description,
          code: this.formData.code,
          isActive: this.formData.isActive,
          hasImageFile: !!this.formData.imageFile,
          hasLogoFile: !!this.formData.logoFile,
          imageUrl: this.formData.imageUrl,
          logoUrl: this.formData.logoUrl,
          editMode: this.editMode,
          currentTenantId: this.currentTenantId
        })

        // Log FormData entries
        console.log('FormData entries:')
        for (const [key, value] of formData.entries()) {
          console.log(`  ${key}:`, value instanceof File ? `File(${value.name}, ${value.size} bytes)` : value)
        }

        if (this.editMode) {
          if (!this.currentTenantId) {
            this.showAlert('danger', 'معرف الفرع مفقود')
            this.saving = false
            return
          }
          // Laravel has issues with PUT requests and multipart/form-data
          // Use POST with _method=PUT as workaround
          formData.append('_method', 'PUT')
          console.log('Sending POST request with _method=PUT to:', process.env.VUE_APP_SERVICE_URL + url.tenants_update + this.currentTenantId)
          const response = await axios.post(
            process.env.VUE_APP_SERVICE_URL + url.tenants_update + this.currentTenantId,
            formData,
            { 
              headers,
              // Important: Let axios handle Content-Type automatically
              transformRequest: [(data) => {
                // Don't transform FormData - return as is
                return data
              }]
            }
          )
          console.log('POST response:', response.data)
        } else {
          console.log('Sending POST request to:', process.env.VUE_APP_SERVICE_URL + url.tenants_create)
          const response = await axios.post(
            process.env.VUE_APP_SERVICE_URL + url.tenants_create,
            formData,
            { 
              headers,
              // Important: Let axios handle Content-Type automatically
              transformRequest: [(data) => {
                // Don't transform FormData - return as is
                return data
              }]
            }
          )
          console.log('POST response:', response.data)
        }

        this.showAlert('success', this.editMode ? 'تم تحديث الفرع بنجاح' : 'تم إنشاء الفرع بنجاح')
        this.closeModal()
        this.loadTenants()
      } catch (error) {
        console.error('Failed to save tenant', error)
        this.showAlert('danger', error.response?.data?.mesAr || 'فشل في حفظ الفرع')
      } finally {
        this.saving = false
      }
    },
    async handleDelete(tenant) {
      const isActive = tenant.isActive || tenant.is_active
      const action = isActive ? 'تعطيل' : 'حذف'
      if (!confirm(`هل أنت متأكد من ${action} الفرع "${tenant.name}"؟`)) return

      try {
        await axios.delete(
          process.env.VUE_APP_SERVICE_URL + url.tenants_delete + tenant.id,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        )
        this.showAlert('success', `تم ${action} الفرع بنجاح`)
        this.loadTenants()
      } catch (error) {
        console.error('Failed to delete tenant', error)
        this.showAlert('danger', error.response?.data?.mesAr || `فشل في ${action} الفرع`)
      }
    },
    async viewStatistics(tenantId) {
      this.showStatisticsModal = true
      this.statisticsLoading = true
      this.statistics = null

      try {
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.tenants_statistics + tenantId,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        )

        if (response.data?.result?.data) {
          this.statistics = response.data.result.data
        } else if (response.data?.data) {
          this.statistics = response.data.data
        } else if (response.data?.result) {
          this.statistics = response.data.result
        } else {
          this.statistics = response.data
        }
      } catch (error) {
        console.error('Failed to load statistics', error)
        this.showAlert('danger', error.response?.data?.mesAr || 'فشل في تحميل الإحصائيات')
      } finally {
        this.statisticsLoading = false
      }
    },
    closeStatisticsModal() {
      this.showStatisticsModal = false
      this.statistics = null
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.currentPage = page
        this.loadTenants()
      }
    },
    showAlert(type, message) {
      this.alert = { show: true, type, message }
      setTimeout(() => {
        this.alert.show = false
      }, 5000)
    }
  },
  mounted() {
    this.loadTenants()
  },
  watch: {
    searchTerm() {
      // Debounce search - reload after user stops typing
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.pagination.currentPage = 1
        this.loadTenants()
      }, 500)
    },
    filterActive() {
      this.pagination.currentPage = 1
      this.loadTenants()
    }
  }
})
</script>

<style scoped>
.enabled-button {
  @apply px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor/90 transition-colors;
}

.popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background-color: #fff;
  padding: 24px;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}
</style>

